import { ChangeEvent, useRef } from 'react';
import { Alert, Box, Chip, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RxCross2 } from 'react-icons/rx';
import { MdInfoOutline } from 'react-icons/md';
import { FiUpload } from 'react-icons/fi';
import Button from 'components/Common/Button';
import { useGetHostSms, useUploadHostSmsLoa } from '@/apis/business/setting/phoneNumbers/hook';
import { HostSms, HostSmsStatus } from '@/apis/business/setting/phoneNumbers/types';
import {
  HOST_SMS_POLL_INTERVAL_MS,
  IN_PROGRESS_STATUSES,
  LOA_ACCEPT,
  formatHostSmsNumber,
  statusPresentation
} from './hostSmsConstants';

type Props = {
  open: boolean;
  record: HostSms | null;
  onClose: () => void;
  onEdit?: (record: HostSms) => void;
};

const BORDER_COLOR = '#E5E7EB';
const FAILED_COLOR = '#DC2626';
const PARTIAL_COLOR = '#C2410C';

const TIMELINE_STEPS = [
  {
    title: 'Order Received',
    description: 'The order request was successfully received and is waiting to start processing.'
  },
  {
    title: 'Processing',
    description: 'The number is currently being processed by the system.'
  },
  {
    title: 'Complete',
    description: 'Hosted messaging was successfully enabled on the number.'
  }
];

const CURRENT_STEP: Partial<Record<HostSmsStatus, number>> = {
  received: 1,
  processing: 2,
  partial: 2,
  complete: 3,
  disconnecting: 3,
  disconnected: 3
};

const currentStepFor = (record: HostSms | null) => {
  if (!record) return 0;
  if (record.status === 'failed') return record.order_details?.order_id ? 2 : 1;
  return CURRENT_STEP[record.status] ?? 0;
};

export default function HostSmsStatusDrawer({ open, record, onClose, onEdit }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const primary = theme.palette.primary.main;
  const loaInputRef = useRef<HTMLInputElement>(null);
  const uploadLoa = useUploadHostSmsLoa();

  const { data } = useGetHostSms(open ? record?.id : null, {
    refetchInterval: (query) =>
      IN_PROGRESS_STATUSES.includes((query.state.data ?? record)?.status as HostSmsStatus)
        ? HOST_SMS_POLL_INTERVAL_MS
        : false
  });
  const current = data ?? record;
  const details = current?.order_details || {};
  const currentStep = currentStepFor(current);
  const isFailed = current?.status === 'failed';
  const isPartial = current?.status === 'partial';
  const isDisconnect = current?.status === 'disconnecting' || current?.status === 'disconnected';
  const orderPlaced = Boolean(details.order_id) && !isFailed && !isDisconnect;
  const loaNeedsUpload = orderPlaced && details.loa_uploaded === false;
  const presentation = statusPresentation(current?.status);

  const handleLoaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file && current) uploadLoa.mutate({ id: current.id, file });
  };

  const failureText = () => {
    if (isPartial) return details.error || 'Hosted messaging was enabled for only part of this order.';
    return details.error || 'Hosted messaging could not be enabled on this number.';
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 620,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Host SMS on Existing Number
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={3}>
        {current && (
          <Stack direction='row' spacing={1.5} alignItems='center' mb={2.5}>
            <Typography fontSize={15} fontWeight={500} color='text.primary'>
              {formatHostSmsNumber(current.number)}
            </Typography>
            <Chip
              label={presentation.label}
              size='small'
              sx={{
                bgcolor: presentation.bg,
                color: presentation.color,
                fontWeight: 500,
                fontSize: 12,
                borderRadius: '999px',
                height: 24,
                px: 0.5
              }}
            />
          </Stack>
        )}

        <Box
          display='flex'
          alignItems='flex-start'
          gap={1}
          mb={3}
          px={2}
          py={1.5}
          borderRadius='6px'
          bgcolor={theme.palette.warning.light}
          color={theme.palette.warning.main}
        >
          <Box display='flex' alignItems='center' height={20} flexShrink={0}>
            <MdInfoOutline size={18} />
          </Box>
          <Box>
            <Typography fontSize={13} fontWeight={600} lineHeight='20px' color='inherit'>
              NOTE:
            </Typography>
            <Typography fontSize={13} lineHeight={1.5} color='inherit'>
              For confirmation, your service provider might send you an email. Please keep an eye on your inbox,
              including your spam or junk folder.
            </Typography>
          </Box>
        </Box>

        {isDisconnect && (
          <Alert severity='info' sx={{ mb: 3, borderRadius: '6px', '& .MuiAlert-message': { fontSize: 13 } }}>
            {current?.status === 'disconnecting'
              ? 'Hosted messaging is being removed from this number.'
              : 'Hosted messaging has been removed from this number.'}
          </Alert>
        )}

        {loaNeedsUpload && (
          <Alert
            severity='warning'
            sx={{ mb: 3, borderRadius: '6px', alignItems: 'center', '& .MuiAlert-message': { fontSize: 13, flex: 1 } }}
            action={
              <Button
                variant='outlined'
                size='small'
                startIcon={<FiUpload size={14} />}
                onClick={() => loaInputRef.current?.click()}
                isPending={uploadLoa.isPending}
                sx={{ textTransform: 'none', borderRadius: '999px', whiteSpace: 'nowrap' }}
              >
                Upload LOA
              </Button>
            }
          >
            The order was placed but the LOA could not be attached
            {details.loa_error ? `: ${details.loa_error}` : '.'}
          </Alert>
        )}
        <input ref={loaInputRef} type='file' hidden accept={LOA_ACCEPT} onChange={handleLoaChange} />

        <Box>
          {TIMELINE_STEPS.map((item, index) => {
            const stepNumber = index + 1;
            const reached = stepNumber <= currentStep;
            const problemStep = (isFailed || isPartial) && stepNumber === currentStep;
            const problemColor = isPartial ? PARTIAL_COLOR : FAILED_COLOR;
            const circleColor = problemStep ? problemColor : primary;
            const isLast = index === TIMELINE_STEPS.length - 1;

            return (
              <Box key={item.title} display='flex' gap={1.5}>
                <Box display='flex' flexDirection='column' alignItems='center'>
                  <Box
                    width={28}
                    height={28}
                    borderRadius='50%'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    flexShrink={0}
                    bgcolor={reached ? circleColor : '#fff'}
                    border={`1px solid ${reached ? circleColor : BORDER_COLOR}`}
                    color={reached ? '#fff' : 'text.primary'}
                    fontSize={13}
                    fontWeight={500}
                  >
                    {stepNumber}
                  </Box>
                  {!isLast && <Box width='1px' flex={1} minHeight={20} bgcolor={BORDER_COLOR} />}
                </Box>
                <Box pt={0.5} pb={isLast ? 0 : 2.5}>
                  <Typography fontSize={14} fontWeight={500} color={problemStep ? problemColor : 'text.primary'}>
                    {item.title}
                  </Typography>
                  <Typography fontSize={12} color='text.secondary' mt={0.25}>
                    {problemStep ? failureText() : item.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {isFailed && onEdit && current && (
        <Box display='flex' justifyContent='flex-end' px={3} py={2} borderTop={`1px solid ${BORDER_COLOR}`}>
          <Button
            variant='contained'
            color='primary'
            onClick={() => onEdit(current)}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: '999px', px: 2.5 }}
          >
            Edit & Resubmit
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
